import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Trash2, MapPin, ImagePlus, X, Loader2, Reply, Image as ImageIcon, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Avatar } from "@/components/site/AvatarUpload";
import { api, formatApiError } from "@/lib/api";
import { CHANNEL_HINTS } from "@/lib/community";

const time = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const fileUrl = (u) => `${process.env.REACT_APP_BACKEND_URL}${u}`;
const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;

function Linkified({ text, mine }) {
  return text.split(URL_RE).map((part, i) => (i % 2
    ? <a key={i} href={part} target="_blank" rel="noopener noreferrer nofollow" className={`underline underline-offset-2 break-all ${mine ? "text-[#FFC48F]" : "text-[#147C88]"}`} data-testid="chat-link">{part}</a>
    : <span key={i}>{part}</span>));
}

const REACTS = [["like", "👍", "Like"], ["fire", "🔥", "Fire"], ["clap", "👏", "Clap"]];

function Reactions({ m, me, mine, onReact }) {
  const r = m.reactions || {};
  return (
    <div className={`mt-1.5 flex flex-wrap gap-1.5 ${mine ? "justify-end" : ""}`} data-testid={`chat-reactions-${m.id}`}>
      {REACTS.map(([k, icon, label]) => {
        const n = (r[k] || []).length;
        const on = (r[k] || []).includes(me.id);
        return (
          <button key={k} onClick={() => onReact(m.id, k)} aria-label={label} aria-pressed={on}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-[background-color,transform,opacity] active:scale-90 ${on ? "bg-[#F08A3C] text-white" : n ? "bg-white border border-[#0B2A4A]/15 text-[#0B2A4A]" : "bg-white/70 border border-[#0B2A4A]/10 text-slate-500 opacity-70 hover:opacity-100"}`}
            data-testid={`chat-react-${k}-${m.id}`}>
            <span className="text-sm leading-none" aria-hidden>{icon}</span>{n > 0 && <span data-testid={`chat-react-count-${k}-${m.id}`}>{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

const dmLink = (email) => `/host/people/${encodeURIComponent(email)}?tab=messages`;

function Quote({ r, mine, onJump }) {
  return (
    <button type="button" onClick={() => onJump(r.id)} className={`mt-1.5 mb-1 block w-full text-left rounded-xl border-l-4 px-3 py-1.5 text-xs ${mine ? "bg-white/10 border-[#F08A3C]" : "bg-[#F5ECD7]/70 border-[#1A9AA8]"}`} data-testid="chat-reply-quote">
      <span className="font-bold block">{r.name}</span>
      <span className="opacity-80 line-clamp-2">{r.text || (r.has_image ? "Photo" : "")}{r.has_image && r.text && " · Photo"}</span>
    </button>
  );
}

function Bubble({ m, me, mine, canDelete, onDelete, onReact, onReply, onJump, dmEmail }) {
  return (
    <div id={`msg-${m.id}`} className={`chat-pop flex items-end gap-2.5 rounded-2xl transition-colors duration-700 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`}>
      <Avatar user={{ name: m.name, avatar: m.avatar }} className="h-8 w-8 shrink-0" />
      <div className="max-w-[78%]">
      <div className={`px-4 py-2.5 shadow-sm rounded-[1.4rem] ${mine ? "bg-[#0B2A4A] text-[#F5ECD7] rounded-br-md" : "bg-white text-[#0B2A4A] border border-[#0B2A4A]/10 rounded-bl-md"}`}>
        <p className={`text-[11px] font-bold ${mine ? "text-[#F08A3C]" : "text-[#147C88]"}`}>{m.name}{m.is_host && <span className="ml-1 rounded-full bg-[#F08A3C] text-white px-1.5 py-px text-[9px] uppercase tracking-wider">Host</span>}{m.works_from && !m.is_host && <span className="font-normal opacity-70"> · {m.works_from}</span>}</p>
        {m.reply_to && <Quote r={m.reply_to} mine={mine} onJump={onJump} />}
        {m.image_url && <a href={fileUrl(m.image_url)} target="_blank" rel="noreferrer"><img src={fileUrl(m.image_url)} alt="Shared in chat" loading="lazy" className="mt-2 max-h-72 w-auto rounded-2xl object-cover" data-testid={`chat-image-${m.id}`} /></a>}
        {m.text && <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap break-words"><Linkified text={m.text} mine={mine} /></p>}
        <p className="mt-1 text-[10px] opacity-60 flex items-center gap-2">{time(m.created_at)}<button onClick={() => onReply(m)} aria-label="Reply" className="inline-flex items-center gap-0.5 opacity-80 hover:opacity-100 font-semibold" data-testid={`chat-reply-btn-${m.id}`}><Reply className="h-3 w-3" /> Reply</button>{dmEmail && !mine && <Link to={dmLink(dmEmail)} className="inline-flex items-center gap-0.5 opacity-80 hover:opacity-100 font-semibold" data-testid={`chat-dm-btn-${m.id}`}><MessageCircle className="h-3 w-3" /> Message privately</Link>}{canDelete && <button onClick={() => onDelete(m.id)} aria-label="Delete message" className="opacity-60 hover:opacity-100" data-testid={`chat-delete-${m.id}`}><Trash2 className="h-3 w-3" /></button>}</p>
      </div>
      <Reactions m={m} me={me} mine={mine} onReact={onReact} />
      </div>
    </div>
  );
}

function useChannel(channel) {
  const [msgs, setMsgs] = useState([]);
  const last = useRef("");
  useEffect(() => {
    let stop = false;
    setMsgs([]);
    last.current = "";
    const pull = () => api.get("/community/messages", { params: { channel } }).then(({ data }) => {
      if (stop) return;
      if (data.length) last.current = data[data.length - 1].created_at;
      setMsgs(data);
    }).catch(() => {});
    pull();
    const t = setInterval(() => { if (!document.hidden) pull(); }, 3000);
    return () => { stop = true; clearInterval(t); };
  }, [channel]);
  return [msgs, setMsgs, last];
}

function Composer({ channel, onSent, replyTo, clearReply }) {
  const [text, setText] = useState("");
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image must be under 8 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/community/uploads", fd);
      setImg(data.url);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const send = async (e) => {
    e.preventDefault();
    if (busy || (!text.trim() && !img)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/community/messages", { channel: channel.id, text, image_url: img || "", reply_to: replyTo?.id || "" });
      onSent(data);
      setText("");
      setImg(null);
      clearReply();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="p-3 sm:p-4 border-t border-[#0B2A4A]/10 bg-white/70 rounded-b-[2rem]">
      {replyTo && <div className="mb-2 flex items-center gap-2 rounded-2xl bg-[#F5ECD7] border-l-4 border-[#1A9AA8] px-3 py-2 text-xs" data-testid="chat-replying-bar"><Reply className="h-4 w-4 text-[#147C88] shrink-0" /><span className="min-w-0 flex-1 truncate"><strong>Replying to {replyTo.name}:</strong> {replyTo.text || (replyTo.image_url ? <><ImageIcon className="inline h-3 w-3" /> Photo</> : "")}</span><button type="button" onClick={clearReply} aria-label="Cancel reply" className="h-6 w-6 rounded-full grid place-items-center hover:bg-white" data-testid="chat-reply-cancel"><X className="h-3.5 w-3.5" /></button></div>}
      {img && <div className="mb-2 relative inline-block" data-testid="chat-image-preview"><img src={fileUrl(img)} alt="To send" className="h-20 rounded-2xl object-cover" /><button type="button" onClick={() => setImg(null)} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#0B2A4A] text-white grid place-items-center" aria-label="Remove image" data-testid="chat-image-remove"><X className="h-3.5 w-3.5" /></button></div>}
      <div className="flex items-end gap-2 rounded-full bg-white border border-[#0B2A4A]/15 pl-2 pr-1.5 py-1.5 shadow-sm focus-within:border-[#1A9AA8]">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="chat-image-input" />
        <button type="button" onClick={() => fileRef.current?.click()} className="h-9 w-9 shrink-0 rounded-full grid place-items-center text-[#147C88] hover:bg-[#1A9AA8]/10" aria-label="Add image" data-testid="chat-image-btn">{busy && !text ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-5 w-5" />}</button>
        <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} rows={1} maxLength={2000} placeholder={`Message #${channel.label} · paste links or add a photo`} className="flex-1 resize-none bg-transparent py-2 text-sm outline-none max-h-32" data-testid="chat-input" />
        <button disabled={busy} className="h-9 w-9 shrink-0 rounded-full bg-[#F08A3C] text-white grid place-items-center hover:bg-[#D96F28] disabled:opacity-60" aria-label="Send" data-testid="chat-send-btn"><Send className="h-4 w-4" /></button>
      </div>
    </form>
  );
}

export function ChatRoom({ channel, me, emails = {} }) {
  const [msgs, setMsgs, last] = useChannel(channel.id);
  const feed = useRef(null);
  useEffect(() => { feed.current?.scrollTo({ top: feed.current.scrollHeight, behavior: "smooth" }); }, [msgs.length]);
  const onSent = (d) => { setMsgs((m) => [...m, d]); last.current = d.created_at; };
  const del = (id) => api.delete(`/community/messages/${id}`).then(() => setMsgs((m) => m.filter((x) => x.id !== id))).catch((e) => toast.error(formatApiError(e)));
  const react = (id, emoji) => api.post(`/community/messages/${id}/react`, { emoji }).then(({ data }) => setMsgs((m) => m.map((x) => (x.id === id ? { ...x, reactions: data.reactions } : x)))).catch((e) => toast.error(formatApiError(e)));
  const [replyTo, setReplyTo] = useState(null);
  const inputFocus = () => setTimeout(() => document.querySelector("[data-testid=chat-input]")?.focus(), 0);
  const onReply = (m) => { setReplyTo(m); inputFocus(); };
  const onJump = (id) => {
    const el = document.getElementById(`msg-${id}`);
    if (!el) { toast.info("That message is older than the visible history"); return; }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("bg-[#F08A3C]/15");
    setTimeout(() => el.classList.remove("bg-[#F08A3C]/15"), 1400);
  };
  return (
    <div className="rounded-[2rem] bg-[#FAF6EE] border border-[#0B2A4A]/10 shadow-xl shadow-[#0B2A4A]/5 flex flex-col h-[72vh] min-h-[500px] overflow-hidden" data-testid={`chat-room-${channel.id}`}>
      <div className="px-5 py-4 bg-[#0B2A4A] text-[#F5ECD7] flex items-center gap-3 grain">
        <img src="/brand/logo-square.jpg" alt="Digital Nomad Nicaragua" className="h-10 w-10 rounded-full bg-white p-0.5 object-cover" />
        <div className="min-w-0"><h3 className="font-semibold text-white truncate"># {channel.label}</h3><p className="text-xs text-[#F5ECD7]/70 truncate">{CHANNEL_HINTS[channel.id]}</p></div>
        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-[#F5ECD7]/80"><span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> Live</span>
      </div>
      <div ref={feed} className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col gap-3 scroll-smooth" data-testid="chat-feed">
        {msgs.length ? msgs.map((m) => <Bubble key={m.id} m={m} me={me} mine={m.user_id === me.id} canDelete={m.user_id === me.id || me.role === "host"} onDelete={del} onReact={react} onReply={onReply} onJump={onJump} dmEmail={me.role === "host" ? emails[m.user_id] : null} />) : <p className="m-auto text-sm text-slate-500" data-testid="chat-empty">No messages yet. Start the conversation.</p>}
      </div>
      <Composer channel={channel} onSent={onSent} replyTo={replyTo} clearReply={() => setReplyTo(null)} />
    </div>
  );
}

export function MembersList({ rows, isHost }) {
  return (
    <div className="rounded-[2rem] bg-white border border-[#0B2A4A]/10 p-5 max-h-[72vh] overflow-y-auto" data-testid="community-members">
      <h3 className="font-semibold text-[#0B2A4A]">Members ({rows.length})</h3>
      <ul className="mt-3 flex flex-col gap-4">
        {rows.map((m) => (
          <li key={m.id} className="flex gap-3" data-testid={`member-${m.id}`}>
            <Avatar user={{ name: m.name, avatar: m.avatar }} className="h-9 w-9 shrink-0" />
            <div className="text-sm"><p className="font-semibold text-[#0B2A4A]">{m.name}</p><p className="text-slate-600">{m.what_i_do}</p><p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{m.works_from}</p>{isHost && m.email && <Link to={dmLink(m.email)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#147C88]" data-testid={`member-dm-btn-${m.id}`}><MessageCircle className="h-3 w-3" /> Message privately</Link>}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
